import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { GradientHeading } from "@/components/ui/gradient-heading";
const About = () => {
  const navigate = useNavigate();
  const handleContactClick = () => {
    navigate('/');
    setTimeout(() => {
      const contactSection = document.querySelector('#contact');
      contactSection?.scrollIntoView({
        behavior: 'smooth'
      });
    }, 100);
  };
  return <div className="min-h-screen">
      <Header />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-subtle"></div>
          <div className="container mx-auto px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <h1 className="text-5xl md:text-6xl font-display font-extralight bg-gradient-to-br from-foreground via-foreground to-primary bg-clip-text text-transparent">
                Über uns – Scholz Interim
              </h1>
              <p className="text-xl text-muted-foreground font-light leading-relaxed">
                Headhunting & Recruiting für Hotellerie & Gastronomie – nachhaltig und passgenau.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-8">
            <div className="max-w-4xl mx-auto space-y-16">
              
              {/* Opening Statement */}
              <div className="text-center space-y-6">
                <p className="text-lg text-foreground leading-relaxed">
                  Wir wissen: In kaum einer Branche ist die richtige Mitarbeiterwahl so entscheidend wie in der Hotellerie. 
                  Gästezufriedenheit hängt unmittelbar von den Menschen ab, die den Betrieb tragen.
                </p>
                <p className="text-lg text-foreground leading-relaxed">
                  Darum besetzen wir keine Stellen „auf dem Papier" – wir verbinden Persönlichkeiten, die wirklich zusammenpassen. 
                  Das reduziert Fluktuation, spart Zeit und sorgt für langfristigen Erfolg.
                </p>
              </div>

              {/* The Scholz Story */}
              <div className="relative">
                {/* Background decorative elements */}
                <div className="absolute -top-12 -left-8 w-32 h-32 rounded-full bg-gradient-to-br from-primary/5 to-secondary/10 blur-2xl"></div>
                <div className="absolute -bottom-8 -right-12 w-24 h-24 rounded-full bg-gradient-to-tl from-primary/10 to-secondary/5 blur-xl"></div>
                
                <div className="relative bg-gradient-to-br from-background to-secondary/5 rounded-3xl p-12 border border-border/30 shadow-elegant">
                  <GradientHeading size="xl" align="center" className="mb-12">
                    Die Scholz Story
                  </GradientHeading>
                  
                  {/* Timeline */}
                  <div className="space-y-12">
                    {/* 2002 Start */}
                    <div className="relative">
                      <div className="flex items-center gap-8">
                        <div className="flex-shrink-0 w-20 h-20 rounded-full bg-gradient-primary flex items-center justify-center text-white font-bold text-lg shadow-glow">
                          2002
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-display font-medium text-foreground mb-3">
                            Der Grundstein
                          </h3>
                          <p className="text-foreground leading-relaxed">
                            Als Michael Scholz 2002 Scholz Interim gründete, brachte er jahrzehntelange Erfahrung als Hotelführungskraft mit. Er erkannte früh:
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Key Insight */}
                    <div className="relative ml-10 pl-12 border-l-2 border-gradient-to-b from-primary/30 to-transparent">
                      <div className="relative bg-white/50 backdrop-blur-sm rounded-2xl p-8 border border-primary/20 shadow-lg">
                        <div className="absolute -left-4 top-8 w-8 h-8 rounded-full bg-primary shadow-lg"></div>
                        <blockquote className="text-2xl font-light italic text-foreground text-center leading-relaxed">
                          „Fachwissen ist wichtig – aber Führungskultur und Menschlichkeit entscheiden über Erfolg."
                        </blockquote>
                        <div className="text-center mt-4">
                          <cite className="text-sm text-muted-foreground font-medium">Michael Scholz, Gründer</cite>
                        </div>
                      </div>
                    </div>
                    
                    {/* Development Phase */}
                    <div className="relative">
                      <div className="flex items-center gap-8">
                        <div className="flex-shrink-0 w-20 h-20 rounded-full bg-gradient-to-br from-secondary to-primary/20 flex items-center justify-center text-foreground font-bold shadow-lg">
                          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-display font-medium text-foreground mb-3">
                            Die Entwicklung
                          </h3>
                          <p className="text-foreground leading-relaxed mb-4">
                            Auf dieser Basis startete er zunächst mit Trainings für Führungskräfte. Dabei wurde schnell deutlich: 
                            Probleme entstehen selten aus fehlender Kompetenz, sondern oft, weil Mensch und Umfeld nicht zusammenpassen.
                          </p>
                          <p className="text-foreground leading-relaxed font-medium">
                            Daraus entwickelte sich unsere Spezialisierung: Headhunting und Personalvermittlung für Hotellerie & Gastronomie – 
                            basierend auf echter Branchenkenntnis und Führungserfahrung.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Today */}
                    <div className="relative">
                      <div className="flex items-center gap-8">
                        <div className="flex-shrink-0 w-20 h-20 rounded-full bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center text-white font-bold text-lg shadow-glow">
                          Heute
                        </div>
                        <div className="flex-1 bg-gradient-to-r from-primary/5 to-transparent rounded-2xl p-6">
                          <h3 className="text-xl font-display font-medium text-foreground mb-3">
                            20+ Jahre Erfolg
                          </h3>
                          <p className="text-foreground leading-relaxed">
                            Seit über 20 Jahren sind wir der Partner, der Hotels und Gastro-Betriebe nicht nur Stellen besetzt, 
                            sondern nachhaltige Teams formt.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Advantages */}
              <div className="space-y-8">
            <GradientHeading size="xl" align="center">
              Ihr Vorteil mit Scholz Interim
            </GradientHeading>
                
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="flex items-start space-x-4 p-6 bg-secondary/5 rounded-xl">
                    <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <span className="text-foreground leading-relaxed">
                      Passgenaue Besetzungen statt „Lebenslauf-Matching"
                    </span>
                  </div>
                  
                  <div className="flex items-start space-x-4 p-6 bg-secondary/5 rounded-xl">
                    <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <span className="text-foreground leading-relaxed">
                      Weniger Fluktuation – mehr Stabilität im Team
                    </span>
                  </div>
                  
                  <div className="flex items-start space-x-4 p-6 bg-secondary/5 rounded-xl">
                    <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <span className="text-foreground leading-relaxed">
                      Zeiteinsparung für Hoteliers – wir finden die Richtigen
                    </span>
                  </div>
                  
                  <div className="flex items-start space-x-4 p-6 bg-secondary/5 rounded-xl">
                    <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <span className="text-foreground leading-relaxed">
                      Über 20 Jahre Erfahrung direkt aus der Branche
                    </span>
                  </div>
                </div>
              </div>

              {/* Closing Statement */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-secondary/5 rounded-3xl"></div>
                <div className="relative text-center space-y-8 py-16 px-8">
                  <div className="max-w-3xl mx-auto">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-primary text-white mb-6 shadow-glow">
                      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-2xl text-foreground leading-relaxed font-light">
                      Seit über 20 Jahren sind wir der Partner, der Hotels und Gastro-Betriebe nicht nur Stellen besetzt, 
                      sondern <span className="font-medium text-primary">nachhaltige Teams formt</span>.
                    </p>
                  </div>
                  
                  <Button 
                    variant="luxury" 
                    size="lg" 
                    className="font-medium shadow-lg hover:shadow-xl transition-all duration-300" 
                    asChild
                  >
                    <a href="/#contact">Kontakt aufnehmen</a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      {/* Footer */}
      
      <Footer />
    </div>;
};
export default About;